import { useEffect, useState, useMemo } from 'react'
import { useAppDispatch, useAppSelector } from '../store/hooks'
import { fetchLabOrders } from '../store/labWorkSlice'
import { fetchVeterinarians } from '../store/settingsSlice'
import { LabOrderListTable } from '../components/lab-work/LabOrderListTable'
import { LabOrderSearch } from '../components/lab-work/LabOrderSearch'
import { NewLabOrderModal } from '../components/lab-work/NewLabOrderModal'
import { Plus } from 'lucide-react'

function formatLabOrderId(num: number): string {
  return `LAB-${String(num).padStart(4, '0')}`
}

export function LabWorkPage() {
  const dispatch = useAppDispatch()
  const { orders, loading } = useAppSelector((s) => s.labWork)

  const [modalOpen, setModalOpen] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')
  const [statusFilter, setStatusFilter] = useState('')

  useEffect(() => {
    dispatch(fetchLabOrders())
    dispatch(fetchVeterinarians())
  }, [dispatch])

  const filteredOrders = useMemo(() => {
    return orders.filter((order) => {
      // Search filter
      if (searchQuery) {
        const q = searchQuery.toLowerCase()
        const labId = formatLabOrderId(order.lab_order_number).toLowerCase()
        const patientName = (order.patient_name || '').toLowerCase()
        const testName = (order.test_name || '').toLowerCase()
        if (!patientName.includes(q) && !labId.includes(q) && !testName.includes(q)) {
          return false
        }
      }
      // Status filter
      if (statusFilter && order.status !== statusFilter) {
        return false
      }
      return true
    })
  }, [orders, searchQuery, statusFilter])

  if (loading) {
    return (
      <div className="p-6 max-sm:p-3" data-testid="lab-work-page">
        <p className="text-[13px] text-text-muted">Loading lab orders...</p>
      </div>
    )
  }

  return (
    <div className="p-6 max-sm:p-3" data-testid="lab-work-page">
      {/* Page header */}
      <div className="flex items-center justify-between mb-4">
        <h1 className="text-[16px] font-semibold text-text-primary">Lab Work</h1>
        <button
          data-testid="new-lab-order-button"
          onClick={() => setModalOpen(true)}
          className="inline-flex items-center gap-1.5 h-7 px-3 rounded-[4px] bg-accent text-white text-[13px] hover:bg-accent-dark transition-colors duration-100 cursor-pointer"
        >
          <Plus size={14} />
          <span className="max-sm:hidden">New Lab Order</span>
        </button>
      </div>

      {/* Search and filters */}
      <div className="mb-4">
        <LabOrderSearch
          searchQuery={searchQuery}
          onSearchChange={setSearchQuery}
          statusFilter={statusFilter}
          onStatusFilterChange={setStatusFilter}
        />
      </div>

      {/* Table */}
      <div className="overflow-x-auto">
        <LabOrderListTable orders={filteredOrders} />
      </div>

      {/* New Lab Order Modal */}
      <NewLabOrderModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
      />
    </div>
  )
}
